import { useEffect, useState } from 'react';
import api from '../api/api';
import styled from 'styled-components';

const Container = styled.section`
  padding: 4rem 2rem;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media (min-width: 1024px) {
    padding: 6rem 10% 4rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Timeline = styled.div`
  position: relative;
  border-left: 2px solid rgba(99, 102, 241, 0.2);
  margin-left: 10px;
  padding-left: 30px;

  @media (min-width: 768px) {
    margin-left: 20px;
    padding-left: 40px;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -37px;
  top: 6px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #0b0f19;
  border: 3px solid #6366f1;
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.8);
  transition: all 0.3s ease;
  z-index: 2;

  @media (min-width: 768px) {
    left: -48px;
    width: 16px;
    height: 16px;
    top: 8px;
  }
`;

const TimelineCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(30, 41, 59, 0.7);
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateX(6px);
    
    & + ${TimelineDot} {
      background: #06b6d4;
      border-color: #06b6d4;
      box-shadow: 0 0 12px rgba(6, 182, 212, 1);
    }
  }

  /* Faz a conexão hover inversa funcionar */
  &:hover ~ div {
    background: #06b6d4;
  }
`;

// Helper para acionar o ponto no hover do card
const ItemWrapper = styled.div`
  &:hover ${TimelineDot} {
    background: #06b6d4;
    border-color: #06b6d4;
    box-shadow: 0 0 12px #06b6d4;
  }
`;

const CompanyName = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.25rem;
`;

const Role = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #818cf8;
  margin-bottom: 0.75rem;
`;

const DateRange = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.05);
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.6;
  white-space: pre-line;
`;

const Empty = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  padding: 4rem 2rem;
  background: rgba(30, 41, 59, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

function Carreira() {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function dadosCarreira() {
      try {
        const resposta = await api.get('/api/carreira');
        setDados(resposta.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    dadosCarreira();
  }, []);

  if (loading) {
    return (
      <Container>
        <Empty>Carregando...</Empty>
      </Container>
    );
  }

  if (dados.length === 0) {
    return (
      <Container>
        <Title>Carreira</Title>
        <Empty>Nenhuma experiência profissional cadastrada ainda.</Empty>
      </Container>
    );
  }

  return (
    <Container id="carreira">
      <Title>Trajetória Profissional</Title>

      <Timeline>
        {dados.map((item) => (
          <ItemWrapper key={item.id}>
            <TimelineItem>
              <TimelineDot />
              <TimelineCard>
                <CompanyName>{item.nomeempresa}</CompanyName>
                <Role>{item.cargo}</Role>
                <DateRange>
                  📅 {item.dataentrada} — {item.datasaida || 'Atual'}
                </DateRange>
                {item.descricaocargo && (
                  <Description>{item.descricaocargo}</Description>
                )}
              </TimelineCard>
            </TimelineItem>
          </ItemWrapper>
        ))}
      </Timeline>
    </Container>
  );
}

export default Carreira;
